import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { api, apiErrorMessage } from "@/lib/api";
import { CheckCircle2, Droplets, LockKeyhole } from "lucide-react";

interface PublicService { id: number; name: string; durationMinutes: number; priceCents: number; currency: string; }
interface PublicLocation { id: number; name: string; timezone: string; currency: string; }
interface AvailabilitySlot { startAt: string; endAt: string; }
interface AddonRow {
  id: number; serviceId: number; name: string; code: string;
  priceCents: number; impactMinutes: number; description: string | null;
}
type ServiceMode = "STATIONARY" | "MOBILE";

export function PublicBookingPage() {
  const [locations, setLocations] = useState<PublicLocation[]>([]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [services, setServices] = useState<PublicService[]>([]);
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [serviceMode, setServiceMode] = useState<ServiceMode>("STATIONARY");
  const [resourceId, setResourceId] = useState<number | null>(null);
  const [resources, setResources] = useState<{ id: number; name: string; type: string }[]>([]);
  const [serviceIds, setServiceIds] = useState<Set<number>>(new Set());
  const [addonIds, setAddonIds] = useState<Set<number>>(new Set());
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [startAt, setStartAt] = useState<string>("");
  const [customer, setCustomer] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [vehicle, setVehicle] = useState({ plate: "", make: "", model: "" });
  const [serviceAddress, setServiceAddress] = useState({
    line1: "", line2: "", city: "", region: "", postalCode: "", countryCode: "",
  });
  const [done, setDone] = useState<{ code: string; manageUrl: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    api.getRaw<PublicLocation[]>("/api/public/v1/locations").then((rows) => {
      setLocations(rows);
      if (rows[0]) setLocationId(rows[0].id);
    }).catch((err) => setError(apiErrorMessage(err, "Could not load locations.")));
  }, []);

  // Mode change resets resource selection.
  useEffect(() => { setResourceId(null); }, [serviceMode]);

  useEffect(() => {
    if (!locationId) return;
    api.getRaw<PublicService[]>(`/api/public/v1/services?locationId=${locationId}`).then((s) => {
      setServices(s as unknown as PublicService[]);
    }).catch((err) => setError(apiErrorMessage(err, "Could not load services.")));
  }, [locationId]);

  useEffect(() => {
    if (!locationId) return;
    api.getRaw<{ id: number; name: string; type: string }[]>(`/api/public/v1/resources?locationId=${locationId}&serviceMode=${serviceMode}`)
      .then((rows) => {
        setResources(rows);
        setResourceId(rows[0]?.id ?? null);
      }).catch(() => setResources([]));
  }, [locationId, serviceMode]);

  useEffect(() => {
    if (!locationId) { setSlots([]); return; }
    const location = locations.find((l) => l.id === locationId);
    if (!location) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ locationId: String(locationId), serviceIds: Array.from(serviceIds).join(","), date, timezone: location.timezone, serviceMode });
      if (resourceId) params.set("resourceId", String(resourceId));
      if (addonIds.size) params.set("addonIds", Array.from(addonIds).join(","));
      const addressReady = serviceAddress.line1.trim() && serviceAddress.city.trim() && serviceAddress.countryCode.trim().length === 2;
      if (serviceMode === "MOBILE" && addressReady) {
        params.set("serviceAddressLine1", serviceAddress.line1);
        params.set("serviceCity", serviceAddress.city);
        params.set("serviceCountryCode", serviceAddress.countryCode);
        if (serviceAddress.line2) params.set("serviceAddressLine2", serviceAddress.line2);
        if (serviceAddress.region) params.set("serviceRegion", serviceAddress.region);
        if (serviceAddress.postalCode) params.set("servicePostalCode", serviceAddress.postalCode);
      }
      setSlotsLoading(true);
      api.getRaw<AvailabilitySlot[]>(`/api/public/v1/availability?${params.toString()}`, controller.signal)
        .then(setSlots).catch((e) => { if (e.name !== "AbortError") setError(apiErrorMessage(e, "Could not load available times.")); })
        .finally(() => setSlotsLoading(false));
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [locationId, resourceId, serviceIds, addonIds, date, locations, serviceMode, serviceAddress]);

  useEffect(() => { setStartAt(""); }, [locationId, resourceId, serviceIds, addonIds, date, serviceMode, serviceAddress]);

  // Add-ons for selected services.
  useEffect(() => {
    if (!locationId || serviceIds.size === 0) { setAddons([]); return; }
    api.getRaw<AddonRow[]>(`/api/public/v1/addons?locationId=${locationId}`)
      .then((rows) => {
        const arr = (rows as unknown as { data?: AddonRow[] })?.data ?? (rows as unknown as AddonRow[]);
        setAddons((arr as AddonRow[]).filter((a) => serviceIds.has(a.serviceId)));
      }).catch(() => setAddons([]));
  }, [locationId, serviceIds]);

  const filteredResources = useMemo(() => resources, [resources]);
  void filteredResources;

  const currency = locations.find((l) => l.id === locationId)?.currency ?? "USD";
  const servicesSubtotal = services.filter((s) => serviceIds.has(s.id)).reduce((s, r) => s + r.priceCents, 0);
  const addonsSubtotal = addons.filter((a) => addonIds.has(a.id)).reduce((s, a) => s + a.priceCents, 0);
  const total = servicesSubtotal + addonsSubtotal;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!locationId || serviceIds.size === 0 || !startAt || !resourceId) {
      setError("Please choose services, a resource, and a time slot.");
      return;
    }
    if (!slots.some((slot) => slot.startAt === startAt)) {
      setStartAt("");
      setError("That time is no longer available. Please choose another slot.");
      return;
    }
    if (serviceMode === "MOBILE" && (!serviceAddress.line1.trim() || !serviceAddress.city.trim() || serviceAddress.countryCode.trim().length !== 2)) {
      setError("Please complete the mobile address, including city and 2-letter country code.");
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        locationId, resourceId, serviceIds: Array.from(serviceIds),
        startAt, serviceMode, customer,
      };
      if (addonIds.size > 0) body.addonIds = Array.from(addonIds);
      if (vehicle.plate) body.vehicle = vehicle;
      if (serviceMode === "MOBILE") body.serviceAddress = serviceAddress;
      const res = await api.postRaw<{ code: string; manageUrl: string | null }>("/api/public/v1/bookings", {
        ...body, idempotencyKey: crypto.randomUUID(),
      });
      setDone({ code: res.code, manageUrl: res.manageUrl });
    } catch (err) {
      setError(apiErrorMessage(err, "Booking failed"));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-svh bg-background">
        <PublicHeader />
        <main className="mx-auto max-w-xl px-4 py-12 md:py-20">
        <Card className="overflow-hidden text-center">
          <CardHeader>
            <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></span>
            <CardTitle className="text-2xl">Booking confirmed</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Your booking code is <span className="font-mono font-semibold">{done.code}</span>.</p>
            {done.manageUrl && (
              <p className="mt-2 text-sm text-muted-foreground">
                Save this link to manage your booking: <a className="text-primary underline" href={done.manageUrl}>{done.manageUrl}</a>
              </p>
            )}
            <Button className="mt-3" variant="outline" onClick={() => { setDone(null); setServiceIds(new Set()); setAddonIds(new Set()); }}>Book another</Button>
          </CardContent>
        </Card></main>
      </div>
    );
  }

  return (
    <div className="min-h-svh bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-4xl px-4 py-8 md:px-6 md:py-12">
      <div className="mb-8 max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">Online booking</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-[-0.035em] md:text-4xl">Professional car care, on your schedule.</h1>
        <p className="mt-3 text-base leading-7 text-muted-foreground">Choose your service, preferred time, and location. We will take care of the rest.</p>
      </div>
      <form onSubmit={submit} className="space-y-5">
        {error && <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}

        <Card>
          <CardHeader><CardTitle className="text-base">Service type</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 sm:flex-row">
              <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-xl border bg-white px-4 py-4 text-sm shadow-sm transition has-[:checked]:border-primary has-[:checked]:bg-primary/[0.04] has-[:checked]:ring-2 has-[:checked]:ring-primary/10">
                <input type="radio" name="mode" value="STATIONARY" checked={serviceMode === "STATIONARY"} onChange={() => setServiceMode("STATIONARY")} />
                <div>
                  <div className="font-medium">In-bay</div>
                  <div className="text-xs text-muted-foreground">You bring the car to the shop.</div>
                </div>
              </label>
              <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-xl border bg-white px-4 py-4 text-sm shadow-sm transition has-[:checked]:border-primary has-[:checked]:bg-primary/[0.04] has-[:checked]:ring-2 has-[:checked]:ring-primary/10">
                <input type="radio" name="mode" value="MOBILE" checked={serviceMode === "MOBILE"} onChange={() => setServiceMode("MOBILE")} />
                <div>
                  <div className="font-medium">Mobile</div>
                  <div className="text-xs text-muted-foreground">We dispatch a team to you.</div>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">When & where</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Location</Label>
              <Select value={locationId?.toString() ?? ""} onChange={(e) => setLocationId(Number(e.target.value))}>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                {locations.length === 0 && <option value="">No locations</option>}
              </Select>
            </div>
            <div>
              <Label>{serviceMode === "MOBILE" ? "Pick a team" : "Pick a bay"}</Label>
              <Select value={resourceId?.toString() ?? ""} onChange={(e) => setResourceId(Number(e.target.value))} disabled={resources.length === 0}>
                {resources.length === 0 && <option value="">No {serviceMode === "MOBILE" ? "teams" : "bays"} available</option>}
                {resources.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <Label>Time</Label>
                 <Select required value={startAt} disabled={slotsLoading} onChange={(e) => setStartAt(e.target.value)}>
                  <option value="">Pick a slot…</option>
                  {slots.map((s) => (
                    <option key={s.startAt} value={s.startAt}>
                      {new Date(s.startAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: locations.find((location) => location.id === locationId)?.timezone })}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            {serviceMode === "MOBILE" && (
              <div className="border-t pt-3">
                <p className="mb-2 text-sm font-medium">Where should we come?</p>
                <div className="space-y-2">
                  <Input required placeholder="Street, building, apartment" value={serviceAddress.line1} onChange={(e) => setServiceAddress({ ...serviceAddress, line1: e.target.value })} />
                  <div className="grid gap-2 sm:grid-cols-2">
                     <Input required placeholder="City" value={serviceAddress.city} onChange={(e) => setServiceAddress({ ...serviceAddress, city: e.target.value })} />
                    <Input placeholder="Postal code" value={serviceAddress.postalCode} onChange={(e) => setServiceAddress({ ...serviceAddress, postalCode: e.target.value })} />
                  </div>
                   <Input required placeholder="Country (e.g. QA)" maxLength={2} pattern="[A-Za-z]{2}" value={serviceAddress.countryCode} onChange={(e) => setServiceAddress({ ...serviceAddress, countryCode: e.target.value.toUpperCase() })} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Services</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {services.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-3 rounded-xl border bg-white px-4 py-3 text-sm shadow-sm transition hover:border-primary/30 has-[:checked]:border-primary has-[:checked]:bg-primary/[0.035]">
                  <input
                    type="checkbox" className="h-4 w-4"
                    checked={serviceIds.has(s.id)}
                    onChange={(e) => {
                      const n = new Set(serviceIds);
                      if (e.currentTarget.checked) n.add(s.id); else n.delete(s.id);
                      setServiceIds(n);
                      setAddonIds(new Set());
                    }}
                  />
                  <span className="flex-1">{s.name}</span>
                  <span className="text-xs text-muted-foreground">{s.durationMinutes}m · {formatMoney(s.priceCents, currency)}</span>
                </label>
              ))}
            </div>
            {serviceIds.size > 0 && addons.length > 0 && (
              <div className="mt-3 border-t pt-3">
                <p className="mb-2 text-sm font-medium">Optional add-ons</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {addons.map((a) => (
                    <label key={a.id} className="flex cursor-pointer items-center gap-3 rounded-xl border bg-white px-4 py-3 text-sm transition hover:border-primary/30 has-[:checked]:border-primary">
                      <input type="checkbox" className="h-4 w-4"
                        checked={addonIds.has(a.id)}
                        onChange={(e) => {
                          const n = new Set(addonIds);
                          if (e.currentTarget.checked) n.add(a.id); else n.delete(a.id);
                          setAddonIds(n);
                        }}
                      />
                      <span className="flex-1">{a.name}</span>
                      <span className="text-xs text-muted-foreground">+{formatMoney(a.priceCents, currency)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">You</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder="First name" required value={customer.firstName} onChange={(e) => setCustomer({ ...customer, firstName: e.target.value })} />
              <Input placeholder="Last name" required value={customer.lastName} onChange={(e) => setCustomer({ ...customer, lastName: e.target.value })} />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input type="email" placeholder="Email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
              <Input placeholder="Phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <Input placeholder="Plate" value={vehicle.plate} onChange={(e) => setVehicle({ ...vehicle, plate: e.target.value })} />
              <Input placeholder="Make" value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} />
              <Input placeholder="Model" value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Summary</CardTitle></CardHeader>
          <CardContent className="text-sm">
            <div className="flex justify-between"><span>Mode</span><span>{serviceMode === "MOBILE" ? "Mobile" : "In-bay"}</span></div>
            <div className="flex justify-between"><span>Add-ons</span><span>{addonIds.size}</span></div>
            <div className="flex justify-between border-t pt-2 text-base">
              <span className="font-medium">Total</span>
              <span className="font-semibold tabular-nums">{formatMoney(total, currency)}</span>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col-reverse items-center justify-between gap-3 rounded-xl border bg-white p-4 shadow-card sm:flex-row">
          <p className="flex items-center gap-2 text-xs text-muted-foreground"><LockKeyhole className="h-3.5 w-3.5" /> Your booking details are transmitted securely.</p>
           <Button type="submit" size="lg" disabled={submitting}>{submitting ? "Booking…" : "Confirm booking"}</Button>
        </div>
      </form>
      </main>
    </div>
  );
}

function PublicHeader() {
  return <header className="border-b border-border/75 bg-white"><div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6"><div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-white"><Droplets className="h-[18px] w-[18px]" /></span><div><p className="text-base font-bold tracking-[-0.025em]">Shinebright</p><p className="text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Premium car care</p></div></div><div className="flex items-center gap-4 text-xs font-semibold"><Link to="/help" className="text-primary hover:underline">Help</Link><p className="hidden text-muted-foreground sm:block">Simple booking · Professional service</p></div></div></header>;
}

function formatMoney(minor: number, currency: string) {
  const zero = new Set(["JPY", "KRW", "VND", "CLP"]);
  const major = minor / (zero.has(currency) ? 1 : 100);
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(major); }
  catch { return `${currency} ${major.toFixed(2)}`; }
}
